package com.evoapps.evoshift.domain.repository

import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.model.WorkplaceSettings

interface WorkplaceRepository {
    // Core Workplace Data
    suspend fun getWorkplace(id: String): Result<Workplace>
    suspend fun getWorkplaceFinancials(workplaceId: String): Result<WorkplaceFinancials>
    /** Creates the workplace with its first [availabilitySlots] (settings.definedAvailabilitySlots); returns the new id. */
    suspend fun createWorkplace(ownerId: String, name: String, availabilitySlots: List<AvailabilitySlot> = emptyList()): Result<String>
    /** Every setting but [WorkplaceSettings.requests], which [updateRequestSettings] writes on its own. */
    suspend fun updateSettings(workplaceId: String, settings: WorkplaceSettings): Result<Unit>
    /**
     * SH-223: settings.requests alone (the rules validate it only when it changes: a whole settings
     * save checking it too would pass their expression budget for an operational manager).
     */
    suspend fun updateRequestSettings(workplaceId: String, requests: com.evoapps.evoshift.domain.model.RequestSettings): Result<Unit> =
        Result.failure(UnsupportedOperationException("updateRequestSettings"))
    /**
     * SH-232: the periods without pay (settings.unpaidPeriods, by user id), with the older list
     * of the people unpaid today (settings.payroll.unpaidUserIds, what older app versions read)
     * and the day they agree on ([asOf], settings.unpaidPeriodsAsOf). Owners and financial
     * managers (firestore.rules).
     */
    suspend fun updateUnpaidWork(
        workplaceId: String,
        periods: Map<String, List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>>,
        unpaidUserIds: List<String>,
        asOf: String
    ): Result<Unit> = Result.failure(UnsupportedOperationException("updateUnpaidWork"))
    suspend fun updateWorkplaceName(workplaceId: String, newName: String): Result<Unit>
    suspend fun updateWorkplaceLocation(workplaceId: String, address: String, location: GeoLocation?): Result<Unit>
    suspend fun updateWorkplaceFinancials(workplaceId: String, financials: WorkplaceFinancials): Result<Unit>
    suspend fun deleteWorkplace(id: String): Result<Unit>

    // Templates
    suspend fun createShiftTemplate(
        workplaceId: String,
        template: ShiftTemplate
    ): Result<String> // Returns new ID

    suspend fun updateShiftTemplate(workplaceId: String, template: ShiftTemplate): Result<Unit>
    /**
     * Deletes the shift template and, in the same batch (SH-190), saves [dayTemplatesWithoutIt]:
     * the day templates that used it, with every occurrence of it removed.
     */
    suspend fun deleteShiftTemplate(
        workplaceId: String,
        templateId: String,
        dayTemplatesWithoutIt: List<DayTemplate> = emptyList()
    ): Result<Unit>
    suspend fun getShiftTemplates(workplaceId: String): Result<List<ShiftTemplate>>
    suspend fun getDayTemplates(workplaceId: String): Result<List<DayTemplate>>

    // Owner Rules
    suspend fun getRecurringCostRules(ownerId: String): Result<List<RecurringCostRule>>
    /** The owner's rules for one workplace: what its financial managers may read (firestore.rules). */
    suspend fun getRecurringCostRulesFor(ownerId: String, workplaceId: String): Result<List<RecurringCostRule>>
    suspend fun createRecurringCostRule(ownerId: String, rule: RecurringCostRule): Result<Unit>
    suspend fun updateRecurringCostRule(ownerId: String, rule: RecurringCostRule): Result<Unit>
    suspend fun deleteRecurringCostRule(ownerId: String, ruleId: String): Result<Unit>
    suspend fun updateDayTemplate(workplaceId: String, template: DayTemplate): Result<Unit>
    suspend fun updateWeeklySkeleton(workplaceId: String, skeleton: Map<Int, String?>): Result<Unit>

    // Zmiana: createDayTemplate zwraca teraz ID utworzonego szablonu
    suspend fun createDayTemplate(workplaceId: String, template: DayTemplate): Result<String>
    suspend fun deleteDayTemplate(workplaceId: String, templateId: String): Result<Unit>
}